/**
 * SettingsPage — S15 tabbed settings (Phase J of the design-takeover).
 *
 * Route: /settings
 * Replaces SettingsRoute's flat SettingsPanel with a five-tab layout.
 *
 * Tabs (authoritative from copy-decisions doc Phase J / S15):
 *   Profile      — display name and email
 *   Cadence      — default session frequency
 *   Notifications — email preferences
 *   Theme        — appearance (theme + mode)
 *   Account      — password, passkeys, TOTP, data export, deletion
 *
 * Note: there is NO separate Security tab. Account absorbs all security settings.
 * The getting-started guide reference "Settings → Security" has been updated
 * to "Settings → Account" (see docs/guides/getting-started.md).
 *
 * A11y:
 *   - Tab list uses role="tablist" with role="tab" / role="tabpanel".
 *   - Arrow-key navigation within the tab list.
 *   - Active tab controls aria-selected, aria-controls, tabIndex.
 *   - Each panel has aria-labelledby pointing at its tab.
 */
import React, { useCallback, useRef, useState } from 'react'
import { AccountSection, SecuritySection } from '../components/AccountSecuritySections.jsx'
import { api } from '../api/client.js'
import { setClaudeApiKey, deleteClaudeApiKey } from '../api/claude.js'
import { getMode, getTheme, setMode, setTheme } from '../theme.js'
import { Button } from '../ui'

// ─── Tab definitions ────────────────────────────────────────────────────────

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'cadence', label: 'Cadence' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'theme', label: 'Theme' },
  { id: 'account', label: 'Account' },
]

// ─── Tab content components ─────────────────────────────────────────────────

function ProfileTab({ user, me }) {
  return (
    <section className="settings-tab__panel">
      <p className="settings-tab__intro">
        Your display name and email. These are what your manager and reports see.
      </p>
      <AccountSection user={user} />
    </section>
  )
}

function CadenceTab() {
  const CADENCE_OPTIONS = [
    { value: 'weekly', label: 'Weekly', desc: 'Once a week.' },
    { value: 'biweekly', label: 'Fortnightly', desc: 'Once every two weeks.' },
    { value: 'monthly', label: 'Monthly', desc: 'Once a month.' },
  ]

  return (
    <section className="settings-tab__panel">
      <p className="settings-tab__intro">
        Your default session frequency. This applies to any pair where you haven&apos;t set a
        custom cadence.
      </p>
      <div className="settings-tab__card">
        <p className="settings-tab__note">
          Cadence settings are managed per pair. Visit a pair&apos;s settings to adjust the
          frequency for that relationship.
        </p>
        <ul className="settings-tab__cadence-list">
          {CADENCE_OPTIONS.map((opt) => (
            <li key={opt.value} className="settings-tab__cadence-item">
              <span className="settings-tab__cadence-label">{opt.label}</span>
              <span className="settings-tab__cadence-desc">{opt.desc}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

function NotificationsTab() {
  return (
    <section className="settings-tab__panel">
      <p className="settings-tab__intro">
        Choose which emails Soundcheck sends you, and how often.
      </p>
      <div className="settings-tab__card">
        <p className="settings-tab__note">
          Notification preferences are coming soon. You&apos;ll be able to control session
          reminders, prep nudges, and digest emails here.
        </p>
      </div>
    </section>
  )
}

function ThemeTab() {
  const [theme, setThemeState] = useState(() => getTheme())
  const [mode, setModeState] = useState(() => getMode())

  function handleThemeChange(next) {
    setTheme(next)
    setThemeState(next)
  }

  function handleModeChange(next) {
    setMode(next)
    setModeState(next)
  }

  const themeOptions = [
    { value: 'default', label: 'Default' },
    { value: 'warm', label: 'Warm' },
    { value: 'forest', label: 'Forest' },
    { value: 'ocean', label: 'Ocean' },
  ]

  const modeOptions = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
  ]

  return (
    <section className="settings-tab__panel">
      <p className="settings-tab__intro">Adjust how Soundcheck looks on your device.</p>
      <div className="settings-tab__card">
        <p className="settings-tab__field-label">Colour theme</p>
        <div className="settings-tab__chip-row" role="radiogroup" aria-label="Colour theme">
          {themeOptions.map((opt) => {
            const active = theme === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => handleThemeChange(opt.value)}
                className={`settings-tab__chip${active ? ' settings-tab__chip--active' : ''}`}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
        <p className="settings-tab__field-label" style={{ marginTop: '16px' }}>Mode</p>
        <div className="settings-tab__chip-row" role="radiogroup" aria-label="Display mode">
          {modeOptions.map((opt) => {
            const active = mode === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => handleModeChange(opt.value)}
                className={`settings-tab__chip${active ? ' settings-tab__chip--active' : ''}`}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function AccountTab({ user, me, onRequestDeletion, onCancelDeletion }) {
  const [exportStatus, setExportStatus] = useState(null)
  const [exportData, setExportData] = useState(null)
  const [exportRequesting, setExportRequesting] = useState(false)
  const [claudeKeyInput, setClaudeKeyInput] = useState('')
  const [claudeKeySaving, setClaudeKeySaving] = useState(false)
  const [claudeKeyError, setClaudeKeyError] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletionLoading, setDeletionLoading] = useState(false)
  const pollRef = useRef(null)

  const hasClaudeKey = me?.hasClaudeKey || false
  const graceDays = parseInt(import.meta.env.VITE_DELETION_GRACE_DAYS || '5', 10)

  const fetchExportStatus = useCallback(async () => {
    try {
      const data = await api.getExport()
      setExportStatus(data.status)
      if (data.status === 'ready') {
        setExportData({ downloadUrl: data.downloadUrl, expiresAt: data.expiresAt })
      }
      return data.status
    } catch {
      return null
    }
  }, [])

  React.useEffect(() => {
    fetchExportStatus()
  }, [fetchExportStatus])

  React.useEffect(() => {
    if (exportStatus === 'pending') {
      pollRef.current = setInterval(async () => {
        const status = await fetchExportStatus()
        if (status !== 'pending') clearInterval(pollRef.current)
      }, 3000)
    } else {
      clearInterval(pollRef.current)
    }
    return () => clearInterval(pollRef.current)
  }, [exportStatus, fetchExportStatus])

  async function handleRequestExport() {
    setExportRequesting(true)
    try {
      await api.requestExport()
      setExportStatus('pending')
      setExportData(null)
    } finally {
      setExportRequesting(false)
    }
  }

  const handleSaveClaudeKey = useCallback(async () => {
    if (!claudeKeyInput.trim()) return
    setClaudeKeySaving(true)
    setClaudeKeyError(null)
    try {
      await setClaudeApiKey(claudeKeyInput.trim())
      setClaudeKeyInput('')
      window.location.reload()
    } catch (err) {
      setClaudeKeyError(err.message || 'Failed to save key')
    } finally {
      setClaudeKeySaving(false)
    }
  }, [claudeKeyInput])

  const handleDeleteClaudeKey = useCallback(async () => {
    setClaudeKeySaving(true)
    try {
      await deleteClaudeApiKey()
      window.location.reload()
    } catch (err) {
      setClaudeKeyError(err.message || 'Failed to remove key')
    } finally {
      setClaudeKeySaving(false)
    }
  }, [])

  const handleRequestDeletion = useCallback(async () => {
    setDeletionLoading(true)
    try {
      await onRequestDeletion()
      setShowDeleteConfirm(false)
    } finally {
      setDeletionLoading(false)
    }
  }, [onRequestDeletion])

  const handleCancelDeletion = useCallback(async () => {
    setDeletionLoading(true)
    try {
      await onCancelDeletion()
    } finally {
      setDeletionLoading(false)
    }
  }, [onCancelDeletion])

  return (
    <section className="settings-tab__panel">
      <p className="settings-tab__intro">
        Manage your password, passkeys, sign-in methods, data export, and account deletion.
      </p>

      {/* Security settings (passkeys + TOTP) live here — not in a separate tab */}
      <SecuritySection user={user} />

      {/* AI assistant */}
      <div className="settings-tab__section">
        <p className="settings-tab__section-label">AI assistant</p>
        <div className="settings-tab__card">
          <p className="settings-tab__note">
            Connect your Anthropic API key to get AI-suggested coaching notes on reflections.
            Your key is stored securely and never shared.
          </p>
          {hasClaudeKey ? (
            <div>
              <div className="settings-tab__field-row">
                <span className="settings-tab__field-label">API key</span>
                <span className="settings-tab__field-value">sk-ant-···configured</span>
              </div>
              <div className="settings-tab__card-footer">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleDeleteClaudeKey}
                  disabled={claudeKeySaving}
                >
                  {claudeKeySaving ? 'Removing…' : 'Remove key'}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="settings-tab__api-key-row">
                <input
                  type="password"
                  placeholder="sk-ant-..."
                  value={claudeKeyInput}
                  onChange={(e) => setClaudeKeyInput(e.target.value)}
                  className="settings-tab__password-input"
                  aria-label="Anthropic API key"
                />
                <Button
                  size="sm"
                  onClick={handleSaveClaudeKey}
                  disabled={claudeKeySaving || !claudeKeyInput.trim()}
                >
                  {claudeKeySaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
              {claudeKeyError && (
                <p className="settings-tab__error">{claudeKeyError}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Data & privacy */}
      <div className="settings-tab__section">
        <p className="settings-tab__section-label">Data &amp; privacy</p>
        <div className="settings-tab__card">
          <p className="settings-tab__note">
            Download a copy of all data Soundcheck holds about you.
          </p>
          <div className="settings-tab__card-footer">
            {exportStatus === 'pending' && (
              <span className="settings-tab__export-pending">Preparing your export…</span>
            )}
            {exportStatus === 'ready' && exportData && (
              <div className="settings-tab__export-ready">
                <a
                  href={exportData.downloadUrl}
                  download
                  className="settings-tab__download-btn"
                >
                  Download your data
                </a>
                <span className="settings-tab__export-expiry">
                  Expires{' '}
                  {new Date(exportData.expiresAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </div>
            )}
            {(exportStatus === 'none' || exportStatus === null) && (
              <Button size="sm" onClick={handleRequestExport} disabled={exportRequesting}>
                {exportRequesting ? 'Requesting…' : 'Request data export'}
              </Button>
            )}
            {exportStatus === 'ready' && (
              <button
                className="settings-tab__link-btn"
                onClick={handleRequestExport}
                disabled={exportRequesting}
              >
                Request new export
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="settings-tab__section">
        <p className="settings-tab__section-label settings-tab__section-label--danger">
          Danger zone
        </p>
        <div className="settings-tab__card settings-tab__card--danger">
          {me?.deletionRequest ? (
            <div>
              <p className="settings-tab__note">
                Account scheduled for deletion on{' '}
                <strong>
                  {new Date(me.deletionRequest.scheduledFor).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </strong>
                .
              </p>
              <button
                className="settings-tab__cancel-deletion-btn"
                onClick={handleCancelDeletion}
                disabled={deletionLoading}
              >
                {deletionLoading ? 'Cancelling...' : 'Cancel deletion'}
              </button>
            </div>
          ) : (
            <div>
              <p className="settings-tab__note">
                Permanently delete your account and all associated data.
              </p>
              <button
                className="settings-tab__danger-btn"
                onClick={() => setShowDeleteConfirm(true)}
              >
                Request account deletion
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirm deletion dialog */}
      {showDeleteConfirm && (
        <div
          className="settings-tab__confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Delete your account?"
        >
          <div className="settings-tab__confirm-card">
            <h2 className="settings-tab__confirm-title">Delete your account?</h2>
            <p className="settings-tab__confirm-body">
              All your sessions and data will be permanently deleted. You have {graceDays} days to
              cancel this request.
            </p>
            <div className="settings-tab__confirm-actions">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleRequestDeletion} disabled={deletionLoading}>
                {deletionLoading ? 'Requesting...' : 'Delete my account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

// ─── SettingsPage ───────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {object} props.user - Auth user (from AuthProvider)
 * @param {object} props.me - Me query result
 * @param {function} props.onRequestDeletion
 * @param {function} props.onCancelDeletion
 */
export function SettingsPage({ user, me, onRequestDeletion, onCancelDeletion }) {
  const [activeTab, setActiveTab] = useState('profile')
  const tabListRef = useRef(null)

  const tabIds = TABS.map((t) => t.id)

  function handleTabKeyDown(e, index) {
    let nextIndex = index
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      nextIndex = (index + 1) % tabIds.length
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      nextIndex = (index - 1 + tabIds.length) % tabIds.length
    } else if (e.key === 'Home') {
      e.preventDefault()
      nextIndex = 0
    } else if (e.key === 'End') {
      e.preventDefault()
      nextIndex = tabIds.length - 1
    } else {
      return
    }
    const tabs = tabListRef.current?.querySelectorAll('[role="tab"]')
    tabs?.[nextIndex]?.focus()
    setActiveTab(tabIds[nextIndex])
  }

  const panelContent = {
    profile: <ProfileTab user={user} me={me} />,
    cadence: <CadenceTab />,
    notifications: <NotificationsTab />,
    theme: <ThemeTab />,
    account: (
      <AccountTab
        user={user}
        me={me}
        onRequestDeletion={onRequestDeletion}
        onCancelDeletion={onCancelDeletion}
      />
    ),
  }

  return (
    <div className="settings-page">
      <div className="settings-page__inner">
        <h1 className="settings-page__title">Settings</h1>

        {/* Tab list */}
        <div
          ref={tabListRef}
          role="tablist"
          aria-label="Settings sections"
          className="settings-page__tablist"
        >
          {TABS.map((tab, index) => {
            const isActive = activeTab === tab.id
            return (
              <button
                key={tab.id}
                id={`settings-tab-${tab.id}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`settings-panel-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                className={`settings-page__tab${isActive ? ' settings-page__tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(e) => handleTabKeyDown(e, index)}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Tab panels */}
        {TABS.map((tab) => (
          <div
            key={tab.id}
            id={`settings-panel-${tab.id}`}
            role="tabpanel"
            aria-labelledby={`settings-tab-${tab.id}`}
            hidden={activeTab !== tab.id}
            tabIndex={0}
          >
            {activeTab === tab.id ? panelContent[tab.id] : null}
          </div>
        ))}
      </div>
    </div>
  )
}
